import React from 'react';
import { Link } from 'react-router-dom';
import { Activity, HeartPulse, ShieldAlert, Footprints, BatteryMedium, Info, ArrowRight, Heart } from 'lucide-react';
import SaveToIdeasButton from '../components/SaveToIdeasButton';

const ExerciseMovementPage = () => {
  return (
    <div className="space-y-8 max-w-4xl mx-auto" data-testid="exercise-movement-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-teal-100 rounded-xl">
              <Activity className="h-8 w-8 text-teal-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-teal-600 mb-1">Integrative Health</p>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
                Exercise & Movement
              </h1>
            </div>
          </div>
          <SaveToIdeasButton
            title="Exercise & Movement"
            category="Lifestyle"
            sourcePage="Exercise & Movement"
            sourceUrl="/exercise-movement"
            variant="compact"
          />
        </div>
      </div>

      {/* Main Content Card */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">

        {/* Section 1: Why movement matters */}
        <div className="p-6 md:p-8 border-b border-slate-100">
          <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">
            Why movement is often discussed in CLL
          </h2>
          <div className="space-y-4 text-slate-700 leading-relaxed">
            <p>
              Regular, gentle physical activity is one of the most consistently studied lifestyle factors in cancer care. Research in people living with blood cancers suggests that staying active may help with fatigue, mood, sleep quality, muscle strength, and overall quality of life.
            </p>
            <p>
              For many people with CLL — especially during Watch and Wait — movement is also a way of doing something positive for their health while their medical team monitors the condition.
            </p>
            <p className="font-medium text-slate-800">
              The goal is not intensity. It is consistency, done at a level that feels right for your body.
            </p>
          </div>
        </div>

        {/* Section 2: Gentle ways to stay active */}
        <div className="p-6 md:p-8 border-b border-slate-100 bg-teal-50/50">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-teal-100 rounded-lg flex-shrink-0 mt-1">
              <Footprints className="h-5 w-5 text-teal-600" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">
                Gentle ways to stay active
              </h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p className="mb-3">Many people with CLL find these approachable and sustainable:</p>
                <ul className="space-y-2 ml-1">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-teal-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Walking — even 10–20 minutes a day, ideally outdoors</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-teal-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Gentle stretching or yoga, focusing on breath and mobility</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-teal-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Light strength work (body weight, resistance bands, light weights) to maintain muscle</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-teal-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Swimming or water-based movement, when infection risk allows</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-teal-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Gardening, cycling on flat ground, tai chi, or dancing — anything enjoyable enough to repeat</span>
                  </li>
                </ul>
                <p className="mt-4 font-medium text-slate-800">
                  Something small done most days tends to matter more than an occasional big effort.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Section 3: Working with fatigue */}
        <div className="p-6 md:p-8 border-b border-slate-100">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-amber-100 rounded-lg flex-shrink-0 mt-1">
              <BatteryMedium className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">
                Working with fatigue, not against it
              </h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p>
                  CLL-related fatigue is real and can vary from day to day. Somewhat counterintuitively, gentle movement is one of the better-supported approaches for cancer-related fatigue — but pacing is everything.
                </p>
                <ul className="space-y-2 ml-1">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Match activity to your energy — a "good day" plan and a "low day" plan</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Break movement into short blocks with rest in between</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Stop while it still feels comfortable, rather than pushing to exhaustion</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Notice patterns — the Symptoms and Mood tracking in this app can help you see what works</span>
                  </li>
                </ul>
                <div className="mt-4 bg-white border border-amber-200 rounded-xl p-4">
                  <p className="text-slate-700">Resting on hard days is not a setback.</p>
                  <p className="font-semibold text-amber-800 mt-1">Listening to your body is part of the practice.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section 4: Sensible precautions */}
        <div className="p-6 md:p-8 border-b border-slate-100 bg-blue-50/50">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0 mt-1">
              <ShieldAlert className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">
                Sensible precautions with CLL
              </h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p className="mb-3">
                  A few CLL-specific considerations are worth discussing with your haematologist before changing your activity:
                </p>
                <ul className="space-y-2 ml-1">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span><span className="font-medium">Low platelets:</span> contact sports and activities with a high fall risk are usually discussed carefully, as bruising and bleeding risk can be higher</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span><span className="font-medium">Enlarged spleen:</span> contact or collision sports are generally avoided while the spleen is enlarged</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span><span className="font-medium">Infection risk:</span> during periods of low immunity, some people prefer outdoor activity or home workouts over busy gyms, and pay extra attention to hand hygiene and wiping down equipment</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span><span className="font-medium">Low haemoglobin (anaemia):</span> breathlessness or dizziness during activity is worth mentioning to your care team</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span><span className="font-medium">During treatment:</span> energy and blood counts can change — activity levels are often adjusted week by week</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Section 5: Getting started gently */}
        <div className="p-6 md:p-8 bg-green-50/60">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-green-100 rounded-lg flex-shrink-0 mt-1">
              <HeartPulse className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">
                A gentle way to begin
              </h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p className="mb-3">Many people start with a simple weekly rhythm and adjust from there:</p>
                <ul className="space-y-2 ml-1">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Most days: a short walk, at a pace where you can still hold a conversation</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>2–3 days: 10–15 minutes of gentle strength or resistance work</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Daily: a few minutes of stretching or breathing practice, perhaps paired with an existing habit</span>
                  </li>
                </ul>
                <p className="mt-4 font-medium text-slate-800">
                  Start below what feels possible, and build slowly. Consistency beats ambition.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Related: Stress & Mental Wellbeing */}
      <Link
        to="/stress-wellbeing"
        className="flex items-center justify-between bg-white rounded-2xl border border-slate-200 p-4 hover:shadow-md hover:border-slate-300 transition-all"
        data-testid="exercise-to-stress-link"
      >
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-rose-50 rounded-xl">
            <Heart className="h-5 w-5 text-rose-600" />
          </div>
          <div>
            <p className="font-semibold text-slate-900 text-sm">Stress & Mental Wellbeing</p>
            <p className="text-xs text-slate-500">Movement and stress management work best together</p>
          </div>
        </div>
        <ArrowRight className="h-5 w-5 text-slate-400 flex-shrink-0" />
      </Link>

      {/* Educational Note Footer */}
      <div className="bg-slate-100 rounded-xl p-5 border border-slate-200">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-slate-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-slate-700 mb-1">Educational note</p>
            <p className="text-sm text-slate-600 leading-relaxed">
              This page is for educational purposes only and is not exercise prescription or medical advice. Everyone's situation is different — always discuss changes to physical activity with your haematologist or care team, especially around blood counts, spleen size, or during treatment.
            </p>
          </div>
        </div>
      </div>

      {/* Bottom Save Button */}
      <div className="flex justify-center pt-4">
        <SaveToIdeasButton
          title="Exercise & Movement"
          category="Lifestyle"
          sourcePage="Exercise & Movement"
          sourceUrl="/exercise-movement"
        />
      </div>
    </div>
  );
};

export default ExerciseMovementPage;
